import { View } from "@towersystems/roam-common-native";
import { ImpactFeedbackStyle, impactAsync } from "expo-haptics";
import { ComponentRef, useCallback, useMemo, useRef } from "react";
import { Platform, ViewStyle } from "react-native";
import {
  GestureEventPayload,
  PanGestureHandler,
  PanGestureHandlerEventPayload,
  PanGestureHandlerGestureEvent,
} from "react-native-gesture-handler";
import Animated, {
  AnimatedRef,
  AnimatedStyleProp,
  Easing,
  runOnJS,
  useAnimatedGestureHandler,
  useAnimatedReaction,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from "react-native-reanimated";
import { DragHandleProps, SortProps, SortingStrategy } from "../types";
import { useSortable } from "../useSortable";
import { arrayShift } from "../utils";
import { GestureContextType } from "./types";
import { holdScale } from "../../../utilities/drag-drop";

type RecomputeXYCoordinatesParams = {
  ctx: GestureContextType;
  event: PanGestureHandlerEventPayload;
};

/**
 * Adapted from: https://github.com/RyanMan56/react-native-drag-to-sort-grid-mono
 */

type Props = SortProps &
  DragHandleProps & {
    //scrollRef?: AnimatedRef<ComponentRef<any>>;
    scrollY: Animated.SharedValue<number>;
    isEditing: boolean;
    onMove?: (
      event: Readonly<GestureEventPayload & PanGestureHandlerEventPayload>
    ) => void;
    onDrop?: (id: string, position: number) => void;
    getSortingStrategy?: (newOrderPosition: number) => SortingStrategy;
    setIsEditing: (newIsEditingState: boolean) => void;
    cancelEditing: () => void;
    renderItem: () => JSX.Element;
  };

const HOLD_ANIMATION_DURATION = 100;

export function DraggableItem({
  // scrollRef,
  scrollY,
  isEditing,
  DragHandleComponent,
  DragHandleContainer,
  onMove,
  onDrop,
  getSortingStrategy = () => arrayShift,
  setIsEditing,
  cancelEditing,
  renderItem,
  ...sortProps
}: Props) {
  const panRef = useRef<any>(null);

  const isHeld = useSharedValue(false);
  const scale = useSharedValue(1);

  const { activeX, activeY, handleReposition } = useSortable(sortProps);

  const {
    id,
    order,
    itemWidth,
    itemHeight,
    margin,
    numColumns,
    offsetX,
    offsetY,
  } = sortProps;

  function clamp(value: number, min: number, max: number) {
    "worklet";

    return Math.min(Math.max(value, min), max);
  }

  function getOrderFromPosition(newX: number, newY: number) {
    "worklet";

    const centerX = newX + itemWidth / 2;
    const centerY = newY + itemHeight / 2;

    const col = Math.floor((centerX - offsetX) / (itemWidth + 2 * margin));
    const row = Math.floor((centerY - offsetY) / (itemHeight + 2 * margin));

    const clampedCol = clamp(col, 0, numColumns - 1);
    const clampedRow = clamp(
      row,
      0,
      Math.floor((order.value.length - 1) / numColumns)
    );

    return { col: clampedCol, row: clampedRow };
  }

  function getOrderPositionFromRowCol(row: number, col: number) {
    "worklet";

    const newOrderPosition = row * numColumns + col;
    return clamp(newOrderPosition, 0, order.value.length - 1);
  }

  function rearrangeOrder(newOrderPosition: number) {
    "worklet";

    const dynamicSortingStrategy = getSortingStrategy(newOrderPosition);

    const newOrder = dynamicSortingStrategy(
      {
        droppedId: id,
        order: order,
      },
      newOrderPosition
    );

    order.value = newOrder;
  }

  function handleRearrange(newX: number, newY: number) {
    "worklet";

    const { col, row } = getOrderFromPosition(newX, newY);

    const newOrderPosition = getOrderPositionFromRowCol(row, col);

    rearrangeOrder(newOrderPosition);

    return newOrderPosition;
  }

  function holdAnimation(value: number) {
    "worklet";

    return withTiming(value, {
      duration: HOLD_ANIMATION_DURATION,
      easing: Easing.inOut(Easing.quad),
    });
  }

  function releaseAnimation(value: number) {
    "worklet";

    return withTiming(value, {
      duration: HOLD_ANIMATION_DURATION,
      easing: Easing.inOut(Easing.quad),
    });
  }

  function recomputeXYCoordinates({
    ctx,
    event,
  }: RecomputeXYCoordinatesParams) {
    "worklet";

    const newX = ctx.startX + event.translationX;
    const newY = ctx.startY + event.translationY + scrollY.value;

    activeX.value = newX;
    activeY.value = newY;

    return {
      newX,
      newY,
    };
  }

  function handleDrop(
    event: PanGestureHandlerEventPayload,
    ctx: GestureContextType
  ) {
    "worklet";
    const coords = recomputeXYCoordinates({ ctx, event });

    const newPosition = handleRearrange(coords.newX, coords.newY);

    if (onDrop) {
      runOnJS(onDrop)(id, newPosition);
    }

    isHeld.value = false;
  }

  const triggerHapticFeedback = useCallback(() => {
    if (Platform.OS !== "web") {
      impactAsync(ImpactFeedbackStyle.Medium);
    }
  }, []);

  const eventHandler = useAnimatedGestureHandler<
    PanGestureHandlerGestureEvent,
    GestureContextType
  >({
    onStart: (event, ctx) => {
      scale.value = holdAnimation(holdScale);
      ctx.startX = activeX.value;
      ctx.startY = activeY.value - scrollY.value;

      triggerHapticFeedback();
      runOnJS(setIsEditing)(true);
      isHeld.value = true;
    },
    onActive: (event, ctx) => {
      if (isHeld.value) {
        if (onMove) {
          runOnJS(onMove)(event);
        }
        const coords = recomputeXYCoordinates({ ctx, event });

        handleRearrange(coords.newX, coords.newY);
      }
    },
    onFinish: () => {
      scale.value = releaseAnimation(1);
    },
    onCancel: handleDrop,
    onEnd: handleDrop,
  });

  useAnimatedReaction(
    () => JSON.stringify(order.value),
    (result, previous) => {
      if (result !== previous && !isHeld.value) {
        handleReposition();
      }
    }
  );

  useAnimatedReaction(
    () => isHeld.value,
    (result, previous) => {
      if (result !== previous) {
        if (!result) {
          runOnJS(setIsEditing)(false);
        }
        handleReposition();
      }
    }
  );

  /**
   * follow pointer as we scroll
   */
  useAnimatedReaction(
    () => scrollY.value,
    (result, previous) => {
      if (result !== previous) {
        if (isHeld.value) {
          activeY.value += result - (previous || 0);
        }
      }
    }
  );

  const isXAxisEnabled = useMemo(() => numColumns > 1, [numColumns]);

  const animatedStyle = useAnimatedStyle<any>(() => {
    const style: AnimatedStyleProp<ViewStyle> = {
      transform: [
        {
          translateY: activeY.value,
        },
        {
          scale: scale.value,
        },
      ],
      zIndex: isHeld.value ? 1 : 0,
    };

    if (isXAxisEnabled) {
      // @ts-ignore
      style.transform?.push({
        translateX: activeX.value,
      });
    }

    return style;
  }, [isXAxisEnabled]);

  const panHandleMarkup = (
    <DragHandleContainer>
      <PanGestureHandler
        //simultaneousHandlers={[scrollRef]}
        ref={panRef}
        onHandlerStateChange={(...args) => {
          return eventHandler(...args);
        }}
        onGestureEvent={(...args) => {
          return eventHandler(...args);
        }}
      >
        <DragHandleComponent />
      </PanGestureHandler>
    </DragHandleContainer>
  );

  return (
    <Animated.View
      style={[
        {
          width: itemWidth,
          height: itemHeight,
          position: "absolute",
          justifyContent: "center",
          alignItems: "center",
        },
        animatedStyle,
      ]}
    >
      <Animated.View
        style={{
          width: itemWidth,
          height: itemHeight,
        }}
      >
        <View
          style={{
            flex: 1,
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {renderItem()}
          {panHandleMarkup}
        </View>
      </Animated.View>
    </Animated.View>
  );
}
